import type { TowerType } from '../core/types';

/**
 * The Restaurant — order a takeout bag, wait for it to cook, open it for a tower.
 *
 * This module is the whole rulebook: what a bag costs, how long it takes, what
 * it can contain and with what odds, and what a duplicate is worth. It is pure
 * (no Phaser, no storage, no clock), so the odds can be unit-tested and the
 * Restaurant scene is left with nothing but presentation.
 *
 * Towers are no longer sold in the Shop — a Normal Takeout Bag is the only way
 * to obtain the ones listed below. See `config/unlocks.ts` for why the retired
 * shop roster (`SHOP_TOWERS`) must nevertheless keep every name it ever had.
 */

/** Hambi coins to order one Normal Takeout Bag. */
export const BAG_COST = 100;

/** Coins refunded when a bag rolls a tower the player already owns. */
export const DUPLICATE_REFUND = 75;

/** Real time a bag takes to prepare, in milliseconds (10 minutes). */
export const BAG_PREP_MS = 10 * 60 * 1000;

/**
 * Rarity bands, rarest last. Purely cosmetic and derived from `weight` by
 * hand — nothing reads a band to decide odds. `Legendary` arrived with the
 * Capybara Event's capsule (spec 2026-09-16); `Epic` and `Ketchuptastic`
 * with Update 1.2's four more capsule Hambis (spec 2026-09-17), which needed
 * seven distinct bands for seven towers.
 */
export type Rarity =
  | 'Common'
  | 'Uncommon'
  | 'Rare'
  | 'Epic'
  | 'Legendary'
  | 'Mythical'
  | 'Ketchuptastic';

/**
 * One slot in a prize table. `weight` is a probability in [0,1]; the whole
 * table sums to exactly 1 (asserted by the unit tests), so `rollTable` can
 * walk it as cumulative bands without normalising.
 *
 * The order is also the DISPLAY order — the odds list under the Order button
 * and the roulette strip both read straight off the array, so the rarest
 * tower sits last in both.
 *
 * Shared with the Capybara Event's capsule, which is a different table with
 * the same shape (see config/capybaraEvent.ts).
 */
export interface BagSlot {
  readonly tower: TowerType;
  readonly weight: number;
  readonly rarity: Rarity;
}

export const TAKEOUT_BAG_ODDS: readonly BagSlot[] = [
  { tower: 'boxer', weight: 0.4, rarity: 'Common' },
  { tower: 'gunner', weight: 0.25, rarity: 'Uncommon' },
  { tower: 'aquasorcerer', weight: 0.15, rarity: 'Rare' },
  { tower: 'firemage', weight: 0.15, rarity: 'Rare' },
  { tower: 'phantom', weight: 0.05, rarity: 'Mythical' },
];

/**
 * Card colour per rarity band (roulette borders + the rarity ribbon).
 *
 * Update 1.2 shifted the middle of the ladder along by one: Uncommon took
 * GREEN, Rare took the blue Uncommon used to wear, and the new Epic took the
 * purple that had been Rare's. One band is one colour everywhere, so the
 * Takeout Bag's Gunner Hambi (Uncommon) turned green and its Aqua Sorcerer and
 * Fire Mage (Rare) turned blue on the same day.
 */
export const RARITY_COLORS: Record<Rarity, number> = {
  Common: 0x8b949c,
  Uncommon: 0x3ac26a,
  Rare: 0x3f8fd8,
  Epic: 0xa05ae0,
  Legendary: 0xf5a623,
  Mythical: 0xe03a2f,
  // Deep ketchup crimson. It has to do two jobs at once: stay clearly DARKER
  // and pinker than Mythical's brighter orange-red, which sits directly above
  // it in the ladder, while still reading as text on the near-black odds panel
  // (`HubScene.buildCapsuleOdds`) — the first try at 0x8a1220 was a true dark
  // red and came out all but invisible there.
  Ketchuptastic: 0xc62a42,
};

/**
 * The Golden Takeout Bag (spec 2026-09-26) — the Restaurant's second page.
 * Same rules as the Normal bag, in a richer bag: 500 coins, five minutes to
 * prepare, and a duplicate refunds 375 (the same 75% share of the price the
 * Normal bag's 75 is of its 100). Six towers since 2026-09-29.
 */
export const GOLDEN_BAG_COST = 500;

/** Coins refunded when a Golden bag rolls a tower the player already owns. */
export const GOLDEN_DUPLICATE_REFUND = 375;

/** Real time a Golden bag takes to prepare, in milliseconds (5 minutes). */
export const GOLDEN_BAG_PREP_MS = 5 * 60 * 1000;

/**
 * The brief's odds (spec 2026-09-29), which are also the display order:
 * 30 / 25 / 20 / 15 / 9.5 / 0.5 — exactly 100, and Ultra Hammy is one bag in
 * two hundred.
 */
export const GOLDEN_BAG_ODDS: readonly BagSlot[] = [
  { tower: 'dagger', weight: 0.3, rarity: 'Uncommon' },
  { tower: 'leafstorm', weight: 0.25, rarity: 'Rare' },
  { tower: 'samurai', weight: 0.2, rarity: 'Epic' },
  { tower: 'masher', weight: 0.15, rarity: 'Legendary' },
  { tower: 'grease', weight: 0.095, rarity: 'Mythical' },
  { tower: 'ultrahammy', weight: 0.005, rarity: 'Ketchuptastic' },
];

/** Every tower a Golden Takeout Bag can contain, in display order. */
export const GOLDEN_BAG_TOWERS: readonly TowerType[] = GOLDEN_BAG_ODDS.map((s) => s.tower);

/** Draw one tower from a Golden bag (same contract as `rollBag`). */
export function rollGoldenBag(rand: () => number): TowerType {
  return rollTable(GOLDEN_BAG_ODDS, rand);
}

/** Milliseconds left on a Golden bag ordered at `orderedAt` (see `bagRemainingMs`). */
export function goldenBagRemainingMs(orderedAt: number, now: number): number {
  return remainingMs(orderedAt, now, GOLDEN_BAG_PREP_MS);
}

/** The slot a tower occupies in the bag, or undefined if the bag can't drop it. */
export function bagSlotFor(tower: TowerType): BagSlot | undefined {
  return TAKEOUT_BAG_ODDS.find((s) => s.tower === tower);
}

/** Every tower a Normal Takeout Bag can contain, in display order. */
export const BAG_TOWERS: readonly TowerType[] = TAKEOUT_BAG_ODDS.map((s) => s.tower);

/** "40%" — the odds of one slot, formatted for the chances list. */
export function oddsLabel(slot: BagSlot): string {
  return `${Math.round(slot.weight * 1000) / 10}%`;
}

/**
 * Draw one tower from the bag. `rand` must return a value in [0,1) and is
 * REQUIRED — the render layer passes the real generator, the tests pass a fixed
 * sequence. It has no default on purpose: `src/config` is part of the pure
 * layer, which the purity test forbids from naming a nondeterministic source.
 *
 * Walks cumulative weight and returns the first slot whose band contains the
 * roll. The trailing return is unreachable arithmetic insurance: it only fires
 * if floating-point drift leaves a roll of 0.9999… fractionally past the last
 * band, and returning the rarest tower is the harmless outcome.
 */
export function rollBag(rand: () => number): TowerType {
  return rollTable(TAKEOUT_BAG_ODDS, rand);
}

/**
 * The cumulative-band walk itself, over any table of the shape above. Split
 * out for the Capybara Event's capsule, which is a second table with the same
 * rules — and the same floating-point trap described below.
 */
export function rollTable(table: readonly BagSlot[], rand: () => number): TowerType {
  const r = Math.min(Math.max(rand(), 0), 1 - Number.EPSILON);
  let acc = 0;
  for (const slot of table) {
    // Rounded to 6 decimals at every step because binary floating point does
    // NOT give 0.4 + 0.25 + 0.15 + 0.15 = 0.95 — it gives 0.9500000000000001,
    // which quietly stole the first ten-millionth of the Mythical band and made
    // a roll of exactly 0.95 come out as a Fire Mage.
    acc = Math.round((acc + slot.weight) * 1e6) / 1e6;
    if (r < acc) return slot.tower;
  }
  return table[table.length - 1].tower;
}

/**
 * Milliseconds left on a bag ordered at `orderedAt`, given `now`.
 *
 * The timer is stored as a wall-clock timestamp, so it keeps running while the
 * game is closed — that is the whole point. It is also why `now < orderedAt`
 * has to be handled: a device clock that jumps backwards (or a save carried to
 * another machine) would otherwise leave a bag with MORE than ten minutes on
 * it, i.e. a timer that never finishes. Such a bag is treated as freshly
 * ordered — capped at the full prep time, never longer.
 */
export function bagRemainingMs(orderedAt: number, now: number): number {
  return remainingMs(orderedAt, now, BAG_PREP_MS);
}

/**
 * The countdown itself, for any wall-clock timer stored as its start stamp —
 * the takeout bag's ten minutes and the capsule's one, which have identical
 * rules and would otherwise have identical code.
 */
export function remainingMs(orderedAt: number, now: number, prepMs: number): number {
  if (!Number.isFinite(orderedAt)) return 0;
  const elapsed = now - orderedAt;
  if (elapsed < 0) return prepMs;
  return Math.max(0, prepMs - elapsed);
}

/** "9:58" — a countdown formatted mm:ss (never negative). */
export function formatCountdown(ms: number): string {
  const total = Math.ceil(Math.max(0, ms) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
